import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DetailPanel, DetailPlaceholder } from "@/components/DataList";
import { TableToolbar } from "@/components/TableToolbar";
import { Badge, Card } from "@/components/ui";
import { ImportPanel } from "@/features/import/ImportPanel";
import { characteristicKinds, characteristicListQuery, qualityKeys, type Characteristic } from "@/features/quality/api";
import { CharacteristicPanel } from "@/features/quality/CharacteristicPanel";
import { cn } from "@/lib/cn";
import { type ExportColumn } from "@/lib/exportXlsx";
import { useCan } from "@/lib/session";
import { useTableView, type TableColumn } from "@/components/TableView";

// Quality → Characteristics: the tenant dictionary of what is inspected.
// A few hundred rows at most: one request (primary key order), a plain
// table, detail in the side panel; bulk changes by import (ADR-034).

interface Search {
  c?: string | undefined;
  import?: "quality_characteristics" | undefined;
}

const invalidateAfterImport = [qualityKeys.characteristics()] as const;

export const Route = createFileRoute("/_app/quality/characteristics")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {};
    if (typeof raw.c === "string" && raw.c) s.c = raw.c;
    if (raw.import === "quality_characteristics") s.import = raw.import;
    return s;
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(characteristicListQuery),
  component: CharacteristicsPage,
});

const kindTone = { measure: "info", attribute: "success", text: "neutral" } as const;

function CharacteristicsPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const charList = useQuery(characteristicListQuery);
  const { isPending } = charList;
  const allRows = useMemo(() => charList.data ?? [], [charList.data]);
  // A few hundred rows, loaded whole: column filters and sort in the browser.
  const charColumns = useMemo<TableColumn<Characteristic>[]>(
    () => [
      { key: "code", header: t("quality.char.code"), type: "text", value: (r) => r.code },
      { key: "name", header: t("quality.char.name"), type: "text", value: (r) => r.name },
      {
        key: "kind",
        header: t("quality.char.kind"),
        type: "enum",
        value: (r) => r.kind,
        options: characteristicKinds.map((k) => ({ value: k, label: t(`quality.kinds.${k}`) })),
      },
      { key: "uom", header: t("quality.char.uom"), type: "text", value: (r) => r.uom?.code },
    ],
    [t],
  );
  const table = useTableView(allRows, charColumns);
  const rows = table.rows;
  const canManage = useCan("quality.plan.manage");
  const canImport = useCan("core.data.import") && canManage;
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const selected = search.c && search.c !== "new" ? (allRows.find((r) => r.code === search.c) ?? null) : null;

  const exportColumns: ExportColumn<Characteristic>[] = [
    { header: "code", kind: "code", value: (r) => r.code },
    { header: "name", kind: "text", value: (r) => r.name },
    { header: "kind", kind: "code", value: (r) => r.kind },
    { header: "uom", kind: "code", value: (r) => r.uom?.code ?? null },
    { header: "decimals", kind: "integer", value: (r) => r.decimals, sum: false },
    { header: "is_active", kind: "boolean", value: (r) => r.is_active },
  ];

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.quality.title")} <span aria-hidden="true">/</span> {t("nav.quality.characteristics")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("quality.char.title")}</h1>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setSearch({ c: "new", import: undefined })}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("quality.char.new")}
          </button>
        )}
      </header>
      <div className="flex min-h-0 grow gap-4">
        <Card aria-label={t("quality.char.title")} className="min-w-0 grow self-start overflow-hidden">
          <TableToolbar
            label={t("quality.char.title")}
            tools={{
              id: "quality-characteristics",
              title: t("quality.char.title"),
              onNew: canManage ? () => setSearch({ c: "new", import: undefined }) : undefined,
              newLabel: t("quality.char.new"),
              onImport: canImport ? () => setSearch({ import: "quality_characteristics", c: undefined }) : undefined,
            }}
            exportColumns={exportColumns}
            rows={rows}
            rowId={(r) => r.code}
            selectedId={search.c}
            onSelect={(r) => setSearch({ c: r.code, import: undefined })}
            onRefresh={() => void charList.refetch()}
            refreshing={charList.isRefetching}
            hasView={table.hasView}
            onClearView={table.clear}
          />
          {table.chips && <div className="border-b border-border-subtle px-3 py-2">{table.chips}</div>}
          {table.popover}
          <div className="overflow-x-auto">
            <table className="tabular w-full min-w-[560px] border-collapse text-[13px]" aria-label={t("quality.char.title")}>
              <thead>
                <tr className="bg-surface-muted text-left text-ink-muted">
                  {table.th("code")}
                  {table.th("name")}
                  {table.th("kind")}
                  {table.th("uom")}
                </tr>
              </thead>
              <tbody>
                {isPending &&
                  [0, 1, 2, 3].map((i) => (
                    <tr key={i} aria-hidden="true" className="border-t border-border-subtle" style={{ height: "var(--row-h)" }}>
                      <td colSpan={4} className="px-4">
                        <span className="block h-4 w-2/3 animate-pulse rounded bg-surface-muted" />
                      </td>
                    </tr>
                  ))}
                {!isPending && rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-ink-muted">
                      <p className="font-medium text-ink">{t("quality.char.emptyTitle")}</p>
                      <p>{t("quality.char.emptyBody")}</p>
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr
                    key={r.code}
                    tabIndex={0}
                    aria-selected={search.c === r.code}
                    onClick={() => setSearch({ c: r.code, import: undefined })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setSearch({ c: r.code, import: undefined });
                      if (e.key === "Escape") setSearch({ c: undefined });
                    }}
                    className={cn(
                      "cursor-pointer border-t border-border-subtle outline-none hover:bg-surface-hover focus-visible:bg-surface-hover",
                      search.c === r.code && "bg-accent-soft hover:bg-accent-soft",
                    )}
                    style={{ height: "var(--row-h)" }}
                  >
                    <td className="px-4 font-mono text-xs font-medium">{r.code}</td>
                    <td className={cn("px-4", !r.is_active && "text-ink-muted line-through decoration-ink-faint")}>{r.name}</td>
                    <td className="px-4">
                      <Badge tone={kindTone[r.kind]}>{t(`quality.kinds.${r.kind}`)}</Badge>
                    </td>
                    <td className="px-4 text-ink-soft">
                      {r.kind === "measure" ? (r.uom?.code ?? "—") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <DetailPanel
          label={search.import ? t("import.title") : t("quality.char.detailLabel")}
          open={!!search.c || !!search.import}
          placeholder={<DetailPlaceholder title={t("quality.char.selectPrompt")} hint={t("po.selectPromptHint")} />}
        >
          {search.import ? (
            <ImportPanel
              entities={["quality_characteristics"]}
              initial="quality_characteristics"
              onClose={() => setSearch({ import: undefined })}
              invalidate={invalidateAfterImport}
            />
          ) : (
            search.c &&
            (search.c === "new" || selected) && (
              <CharacteristicPanel
                key={search.c}
                row={selected}
                onClose={() => setSearch({ c: undefined })}
                onCreated={(code) => setSearch({ c: code })}
              />
            )
          )}
        </DetailPanel>
      </div>
    </div>
  );
}
